/* eslint-disable arrow-body-style */
"use client";

import type { HTMLAttributes, MouseEvent, ReactNode, FC, Ref } from "react";
import { mergeClasses } from "@huub-web/utils";

import type {
  TrackingProps,
  EdgesPropsWithOutClassNames,
  IconProp,
} from "../../props";
import { Card, type CardProps } from "../Card/Card";
import { Text, type TextProps } from "../Text/Text";
import { IconButton, type IconButtonProps } from "../IconButton/IconButton";
import { useHover } from "../../hooks/useHover";
import { Flex } from "../Flex";
import { Button, type ButtonProps } from "../Button";
import { Icon } from "../Icon";
import { Edge } from "../Edges";
import type { IconName } from "../../theme/types";

import {
  actionButtonStyle,
  cardContainerStyle,
  footerFlexStyle,
} from "./ActionCard.css";

export type ActionCardProps = HTMLAttributes<
  HTMLDivElement | HTMLButtonElement | HTMLAnchorElement
> &
  IconProp &
  EdgesPropsWithOutClassNames &
  Pick<CardProps, "edgeDirection" | "disabled" | "applyMotion" | "padding"> & {
    title?: string;
    body?: string;
    value?: string;
    elevated?: boolean;
    variant?: "primary" | "secondary" | "prompt";
    border?: CardProps["border"];
    buttonIcon?: IconName;
    href?: string;
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<HTMLDivElement | HTMLButtonElement | HTMLAnchorElement>;
  } & TrackingProps;

/**
 * ActionCard is a card component that can be used to display information and a single action.
 */
export const ActionCard: FC<ActionCardProps> = ({
  title,
  body,
  value,
  leading,
  trailing,
  variant = "primary",
  border = "default",
  buttonIcon,
  edgeDirection,
  elevated = false,
  disabled = false,
  href,
  className,
  onClick,
  ref,
  ...props
}) => {
  const isInteractive = !!onClick || !!href;

  const { handleEnter, handleLeave, hasHover } = useHover({ disabled });

  const handleClick = (
    e: MouseEvent<HTMLDivElement | HTMLButtonElement | HTMLAnchorElement>,
  ) => {
    onClick?.(e);
    // TODO: Tracking
  };

  const buttonTrailing: ReactNode =
    variant !== "prompt" && buttonIcon ? <Icon icon={buttonIcon} /> : undefined;

  const cardVariant: CardProps["variant"] = "primary";

  const buttonLeading: ReactNode =
    variant === "prompt" ? <Icon icon="aiFilled" /> : undefined;

  const titleVariant: {
    variant: TextProps["variant"];
    bold?: boolean;
  } = (() => {
    if (variant === "primary") {
      return { variant: "displayS" };
    }
    return variant === "secondary"
      ? { variant: "bodyL", bold: true }
      : { variant: "bodyL" };
  })();

  const bodyVariant: {
    variant: TextProps["variant"];
    color?: TextProps["color"];
  } = (() => {
    if (variant === "secondary") {
      return { variant: "bodyM" };
    }
    return { variant: "bodyM", color: "currentColor50" };
  })();

  const buttonVariant: {
    variant: ButtonProps["variant"];
    size?: ButtonProps["size"] | IconButtonProps["size"];
  } = (() => {
    if (variant === "prompt") {
      return { variant: "primary" };
    }
    return value
      ? { variant: "primary", size: "s" }
      : { variant: "tertiary", size: "s" };
  })();

  return (
    <Card
      ref={ref as Ref<HTMLDivElement>}
      as={isInteractive ? (href ? "a" : "button") : "div"}
      className={mergeClasses(cardContainerStyle, className)}
      onClick={isInteractive ? handleClick : undefined}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      border={border}
      variant={cardVariant}
      elevated={elevated}
      edgeDirection={edgeDirection}
      href={href}
      gap={edgeDirection === "horizontal" ? "l" : undefined}
      disabled={isInteractive && disabled}
      {...props}
    >
      {leading && <Edge>{leading}</Edge>}

      {(title || body) && (
        <Flex direction="column" gap="xs" grow>
          {title && (
            <Text
              variant={titleVariant.variant}
              bold={titleVariant.bold}
              markdown
            >
              {title}
            </Text>
          )}
          {body && (
            <Text
              variant={bodyVariant.variant}
              color={bodyVariant.color}
              markdown
            >
              {body}
            </Text>
          )}
        </Flex>
      )}

      {isInteractive && (
        <Flex
          className={footerFlexStyle}
          justify={trailing ? "space-between" : "flex-end"}
          align="center"
        >
          {trailing}

          {value ? (
            <Button
              as="div"
              trailing={buttonTrailing}
              leading={buttonLeading}
              variant={buttonVariant.variant}
              size={buttonVariant.size as ButtonProps["size"]}
              active={hasHover}
              className={actionButtonStyle}
            >
              {value}
            </Button>
          ) : (
            <IconButton
              as="div"
              icon="chevronRight"
              className={actionButtonStyle}
              variant={buttonVariant.variant}
              size={buttonVariant.size as IconButtonProps["size"]}
              active={hasHover}
              rounded
            />
          )}
        </Flex>
      )}
    </Card>
  );
};
